import { useEffect, memo } from 'react';

import { isAccessEnded } from '../../helpers/utils/functions';
import MainLayout from '../../shared/common/components/layouts/MainLayout';
import Loader from '../../shared/common/components/loader/Loader';
import CreateMetricsModal from '../../shared/common/components/reports/CreateMetricsModal';
import { listKeysGroupingsOrder as listKeysGroupings, listKeysMetricsOrder as listKeysMetrics } from '../../shared/common/components/reports/modal-settings/index';
import ReportChart from '../../shared/common/components/reports/chart/ReportChart';
import ReportTable from '../../shared/common/components/reports/report-table';
import FirstRegIcon from '../../shared/common/components/reports/params/attribution/icons/FirstRegIcon';
import LastRegIcon from '../../shared/common/components/reports/params/attribution/icons/LastRegIcon';
import TypeIcon from '../../shared/common/components/reports/params/attribution/icons/TypeIcon';
import Params from '../../shared/common/components/reports/params/Params';
import UniversalReportTitle from '../../shared/common/components/reports/un-title/UniversalReportTitle';
import { chartDetails } from '../../shared/common/constants/details';
import { createAttrItems } from '../../shared/common/constants/report-constants';
import { useTypedSelector } from '../../shared/common/hooks/useTypedSelector';
import { useReportPageInit } from '../../shared/common/hooks/useReportPageInit';

interface MetricListItem {
    dataKey: string;
    name?: string;
    label?: string;
    [key: string]: unknown;
}

interface GroupingListItem {
    dataKey: string;
    name?: string;
    label?: string;
    [key: string]: unknown;
}

// Дефолтные метрики для Order отчёта (dataKey уже содержит суффикс _2)
export const defaultMetricsOrder = [
    'orders_count_2',
    'orders_sum_2',
    'payments_count_2',
    'payments_sum_2',
    'orders_to_forders_converse_2',
].map((dataKey) => listKeysMetrics.find((m: MetricListItem) => m.dataKey === dataKey))
 .filter((item): item is MetricListItem => item !== undefined);

// Дефолтные группировки для Order отчёта (dataKey уже содержит суффикс _2)
export const defaultGroupingsOrder = [
    'reg_utm_source_2',
    'reg_utm_medium_2',
    'reg_utm_campaign_2',
].map((dataKey) => listKeysGroupings.find((g: GroupingListItem) => g.dataKey === dataKey))
 .filter((item): item is GroupingListItem => item !== undefined);

// Для числовых отчётов
export const defaultMetricsOrderNumber = [
    'orders_count_2',
].map((dataKey) => listKeysMetrics.find((m: MetricListItem) => m.dataKey === dataKey))
 .filter((item) => item !== undefined);

export const defaultGroupingNumber = [
    'order_date_y_2',
].map((dataKey) => listKeysGroupings.find((g: GroupingListItem) => g.dataKey === dataKey))
 .filter((item) => item !== undefined);

const OrderReports = () => {
    // Используем универсальный хук для инициализации страницы отчёта
    const { isChartLoading } = useReportPageInit({
        defaultMetrics: defaultMetricsOrder,
        defaultGroupings: defaultGroupingsOrder,
    });
    
    useEffect(() => {
        document.body.style.overflow = 'scroll';
        document.body.style.overflowX = 'auto';
        
        return () => {
            document.body.style.overflow = '';
            document.body.style.overflowX = '';
        };
    }, []);
    
    const attrItems = createAttrItems(<TypeIcon />, <FirstRegIcon />, <LastRegIcon />);
    const { isCreateMetricsVisible } = useTypedSelector((state) => state.reports);
    const accessEnded = isAccessEnded();
    
    return (
        <>
            <MainLayout pageTitle="Отчёт по заказам">
                <div className="page-wrapper">
                    {accessEnded ? (
                        <iframe
                            style={{ border: 'none', width: '990px', float: 'left', minHeight: '100vh', marginTop: '2vh' }}
                            src={`https://doc.scananalytics.ru/oshibki-i-preduprezhdeniya/dostup-k-otchetu-po-zakazam-zakryt/?timestamp=${new Date().getTime()}`}
                            width="100%"
                            height="100%"
                        />
                    ) : (
                        <div className="page-content">
                            <UniversalReportTitle />
                            <Params
                                attributionItems={attrItems}
                                detailsItems={chartDetails}
                                filtersFilter={[
                                    'Пользователи',
                                    'Регистрации',
                                    'Заказы',
                                    'Платежи',
                                ]}
                            />
                            <Loader loading={isChartLoading}>
                                <ReportChart reportType="order" />
                            </Loader>
                            <ReportTable reportType="order" />
                        </div>
                    )}
                </div>
            </MainLayout>

            {isCreateMetricsVisible && (<CreateMetricsModal title="Создание метрики в отчете по заказам" />)}
        </>
    );
};

export default memo(OrderReports);
